// src/pages/StepByStepDemo.tsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import StepByStepMath from '../components/Exercise/StepByStep/StepByStepMath';
import {
  linearEquationTemplate,
  quadraticEquationTemplate,
  fractionArithmeticTemplate,
  systemOfEquationsTemplate
} from '../components/Exercise/StepByStep/mathExerciseTemplates';

const StepByStepDemo: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('linear_equation');
  const [difficulty, setDifficulty] = useState(1);
  const [currentExercise, setCurrentExercise] = useState<any>(null);

  const templates = {
    linear_equation: linearEquationTemplate,
    quadratic_equation: quadraticEquationTemplate,
    fraction_arithmetic: fractionArithmeticTemplate,
    system_2x2: systemOfEquationsTemplate
  };

  const generateNewExercise = () => {
    const template = templates[selectedTemplate as keyof typeof templates];
    const exercise = template.generator(difficulty);
    setCurrentExercise(exercise);
  };

  const handleSubmit = (answers: string[], steps: any[]) => {
    console.log('Exercise completed!', { answers, steps });
    alert('🎉 Tehtävä suoritettu! Hienoa työtä!');
  };

  const handleTemplateChange = (event: SelectChangeEvent) => {
    setSelectedTemplate(event.target.value);
  };

  const handleDifficultyChange = (event: SelectChangeEvent) => {
    setDifficulty(Number(event.target.value));
  };

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h3" gutterBottom textAlign="center">
          🧮 Vaiheittainen matematiikka
        </Typography>
        <Typography variant="h6" color="text.secondary" textAlign="center" mb={4}>
          Opi ratkaisemaan matematiikan tehtäviä vaihe vaiheelta
        </Typography>

        {/* Exercise Generator Controls */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              📝 Luo uusi tehtävä
            </Typography>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tehtävätyyppi</InputLabel>
                  <Select
                    value={selectedTemplate}
                    label="Tehtävätyyppi"
                    onChange={handleTemplateChange}
                  >
                    <MenuItem value="linear_equation">
                      📏 Lineaarinen yhtälö
                    </MenuItem>
                    <MenuItem value="quadratic_equation">
                      📐 Toisen asteen yhtälö
                    </MenuItem>
                    <MenuItem value="fraction_arithmetic">
                      🍕 Murtoluvut
                    </MenuItem>
                    <MenuItem value="system_2x2">
                      📊 Yhtälöpari
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Vaikeustaso</InputLabel>
                  <Select
                    value={difficulty.toString()}
                    label="Vaikeustaso"
                    onChange={handleDifficultyChange}
                  >
                    <MenuItem value={1}>⭐ Helppo</MenuItem>
                    <MenuItem value={2}>⭐⭐ Keskitaso</MenuItem>
                    <MenuItem value={3}>⭐⭐⭐ Vaikea</MenuItem>
                    <MenuItem value={4}>⭐⭐⭐⭐ Ekspertti</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={generateNewExercise}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    }
                  }}
                >
                  🎲 Luo tehtävä
                </Button>
              </Grid>
            </Grid>

            {/* Template Info */}
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                ℹ️ Tehtävätyyppi: {templates[selectedTemplate as keyof typeof templates].name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {templates[selectedTemplate as keyof typeof templates].description}
              </Typography>
              <Box mt={1}>
                <Chip label={`Vaikeustaso: ${difficulty}/4`} color="primary" variant="outlined" />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Exercise Display */}
        {currentExercise ? (
          <Card elevation={3}>
            <CardContent>
              <StepByStepMath
                config={currentExercise}
                onSubmit={handleSubmit}
              />
            </CardContent>
          </Card>
        ) : (
          <Card elevation={1}>
            <CardContent>
              <Box textAlign="center" py={8}>
                <Typography variant="h4" color="text.secondary" gutterBottom>
                  🎯 Valitse tehtävätyyppi ja paina "Luo tehtävä"
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Vaiheittaiset matematiikan tehtävät auttavat oppimaan ratkaisuprosessin
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <Box mt={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🎓 Oppimisen edut
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Oppit ratkaisuprosessin vaihe vaiheelta<br/>
                    • Saat välitöntä palautetta jokaisesta vaiheesta<br/>
                    • Vihjeet auttavat eteenpäin jumissa<br/>
                    • Harjoittelet systemaattista ajattelua
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🔧 Ominaisuudet
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Mukautuva vaikeustaso<br/>
                    • Interaktiivinen vihje-järjestelmä<br/>
                    • Visuaalinen edistymisen seuranta<br/>
                    • Automaattinen vastausten tarkistus
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default StepByStepDemo;